import { useCallback, useEffect, useState } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

// How far (px) to inset the arrows from the map edge, and how far the arrow
// tip sits from the centre of the icon badge.
const EDGE_PADDING = 30
const ARROW_OFFSET = 26

// Renders an arrow at the edge of the map for every active marker that is
// currently outside the viewport, pointing toward it. The icon stays upright
// while only the arrow rotates. Tapping an indicator pans the marker into view.
export default function OffScreenIndicators ({ markers }) {
  const map = useMap()
  const [indicators, setIndicators] = useState([])

  const recompute = useCallback(() => {
    const size = map.getSize()
    const bounds = map.getBounds()
    const centre = size.divideBy(2)
    const halfW = Math.max(size.x / 2 - EDGE_PADDING, 0)
    const halfH = Math.max(size.y / 2 - EDGE_PADDING, 0)

    const next = []
    for (const marker of markers) {
      const latlng = L.latLng(marker.location)
      if (bounds.contains(latlng)) continue

      const point = map.latLngToContainerPoint(latlng)
      const dx = point.x - centre.x
      const dy = point.y - centre.y
      // Push the point onto the padded viewport rectangle along the
      // centre→marker ray so the arrow hugs whichever edge it exits.
      const scale = Math.min(
        halfW / (Math.abs(dx) || 1e-6),
        halfH / (Math.abs(dy) || 1e-6)
      )
      next.push({
        key: marker.key,
        icon: marker.icon,
        location: marker.location,
        x: centre.x + dx * scale,
        y: centre.y + dy * scale,
        angle: (Math.atan2(dy, dx) * 180) / Math.PI
      })
    }
    setIndicators(next)
  }, [map, markers])

  useEffect(() => {
    recompute()
    map.on('move zoom resize viewreset', recompute)
    return () => map.off('move zoom resize viewreset', recompute)
  }, [map, recompute])

  return (
    <div className='pointer-events-none absolute inset-0 z-[1000]'>
      {indicators.map(({ key, icon, location, x, y, angle }) => (
        <button
          key={key}
          type='button'
          aria-label='Show marker'
          onClick={() => map.panTo(location, { animate: true })}
          className='pointer-events-auto absolute'
          style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
        >
          <div className='relative h-11 w-11'>
            <div
              className='absolute inset-0 grid place-items-center'
              style={{ transform: `rotate(${angle}deg)` }}
            >
              <svg
                width='14'
                height='14'
                viewBox='0 0 14 14'
                style={{ transform: `translateX(${ARROW_OFFSET}px)` }}
                className='drop-shadow'
              >
                <polygon points='0,0 14,7 0,14' fill='#000' />
              </svg>
            </div>
            <div className='absolute inset-0 grid place-items-center rounded-full border-2 border-black bg-white shadow'>
              <img src={`${process.env.PUBLIC_URL}/${icon}`} alt='' className='h-7 w-7' />
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}
